import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, take, takeUntil } from 'rxjs/operators';

import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, Inject, Optional, makeStateKey, TransferState } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ContentService } from '@core/services/content.service';
import { LoggingService } from '@core/services/logging.service';
import { SearchService } from '@core/services/search.service';
import { SettingsService } from '@core/services/settings.service';
import { ProductDetailViewModel } from '@models/api-search.models';
import { PageViewModel as UmbracoPage } from '@models/api.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RESPONSE } from '../../../express.tokens';
import { Response as ExpressResponse } from 'express';

interface ProductDetailResponse extends UmbracoPage {
    product: ProductDetailViewModel;
}

const productPageResponseKey = makeStateKey<ProductDetailResponse>('productPageResponse');

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class ProductDetailResolveService {
    private readonly unsubscribe: Subject<void> = new Subject<void>();
    private contentResponse: HttpResponse<UmbracoPage> | null = null;
    private hasInit: boolean = false;

    constructor(
        private http: HttpClient,
        private loggingService: LoggingService,
        private settingsService: SettingsService,
        private contentService: ContentService,
        public searchService: SearchService,
        private state: TransferState,
        @Optional()
        @Inject(RESPONSE)
        private response?: ExpressResponse
    ) {}

    public resolve(route: ActivatedRouteSnapshot): Observable<ProductDetailResponse | UmbracoPage> {
        const ssrResponse: ProductDetailResponse | undefined = this.state.get(productPageResponseKey, undefined);
        // Use response from SSR if available.
        if (!this.hasInit && ssrResponse) {
            return this.handlePageResponse(of(ssrResponse));
        }
        // Resolve by calling url from angular (after initial load / non-ssr mode)
        const productId = route.params.id;
        return this.handlePageResponse(this.resolveUrl('product-detail-page', productId));
    }

    private handlePageResponse(
        page: Observable<ProductDetailResponse>
    ): Observable<ProductDetailResponse | UmbracoPage> {
        this.hasInit = true;
        return page.pipe(
            take(1),
            catchError(err => {
                if (err && err.status === 404 && err.error !== undefined) {
                    return this.notFoundPage();
                }
                return this.serverErrorPage(err);
            })
        );
    }

    private resolveUrl(page: string, productid: string): Observable<ProductDetailResponse> {
        const contentResponse = this.contentResponse
            ? of(this.contentResponse)
            : this.http.get<UmbracoPage>(`content/page/${page}`, { observe: 'response' });
        return forkJoin([contentResponse, this.searchService.getProduct(productid)]).pipe(
            take(1),
            switchMap(([content, product]) => {
                // Cache content response
                this.contentResponse = content;
                const response = {
                    product,
                    ...content.body
                };
                this.state.set(productPageResponseKey, response);
                return of(response);
            })
        );
    }

    private notFoundPage(): Observable<UmbracoPage> {
        return this.settingsService.settings$.pipe(
            untilDestroyed(this),
            take(1),
            switchMap(({ pageReferences }) => {
                if (this.response) {
                    this.response.status(404);
                }

                return this.contentService.getContent(pageReferences?.notFoundErrorPage?.name || '404');
            })
        );
    }

    private serverErrorPage(err: HttpErrorResponse): Observable<UmbracoPage> {
        this.loggingService.logMessage('Page Resolve Error', `${err.name}(${err.status}) - ${err.message}`);
        return this.settingsService.settings$.pipe(
            take(1),
            switchMap(settings => {
                const serverErrorUrl = settings.pageReferences?.serverErrorPage?.url;
                return serverErrorUrl ? this.http.get<UmbracoPage>(serverErrorUrl) : EMPTY;
            }),
            takeUntil(this.unsubscribe)
        );
    }
}
